
import React, { useState } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Loader2, ClipboardCopy } from "lucide-react";

const employeeSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  full_name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  company_name: z.string().default("IT Management Solutions"),
  role: z.string().default("admin"),
});

type EmployeeFormValues = z.infer<typeof employeeSchema>;

interface EmployeeFormProps {
  onEmployeeAdded: () => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ onEmployeeAdded }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      email: "",
      full_name: "",
      company_name: "IT Management Solutions",
      role: "admin",
    },
  });

  const generateTempPassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(tempPassword);
    toast({
      title: "Password copied",
      description: "Temporary password copied to clipboard",
    });
  };

  const handleSubmit = async (values: EmployeeFormValues) => {
    setIsSubmitting(true);
    
    try {
      console.log("Adding new employee:", values);
      
      // First check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from("client_onboarding")
        .select("id")
        .eq("email", values.email)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking existing user:", checkError);
        throw checkError;
      }
      
      if (existingUser) {
        toast({
          title: "User already exists",
          description: "An account with this email already exists in the system.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Generate a random password for the new employee
      const generatedPassword = generateTempPassword();
      setTempPassword(generatedPassword); // Store the password for display
      setIsPasswordVisible(true); // Show the password in the UI
      console.log("Generated temporary password:", generatedPassword);
      
      // Create auth account first
      console.log("Creating auth account for employee...");
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: generatedPassword,
        options: {
          data: {
            full_name: values.full_name,
            company: values.company_name,
            role: values.role
          }
        }
      });

      if (authError) {
        console.error("Error creating auth account:", authError);
        throw authError;
      }

      // Insert new employee record
      console.log("Inserting new employee record...");
      const { error: insertError } = await supabase
        .from("client_onboarding")
        .insert({
          email: values.email,
          full_name: values.full_name,
          company_name: values.company_name,
          role: values.role,
          status: "verified" // Employees are automatically verified
        });

      if (insertError) {
        console.error("Error inserting employee record:", insertError);
        throw insertError;
      }

      console.log("Employee added successfully");
      toast({
        title: "Employee added successfully",
        description: "The employee can now log in to the portal after email verification.",
      });
      
      // Only reset the form, not close the dialog
      form.reset();
      onEmployeeAdded();
      
    } catch (error: any) {
      console.error("Error adding employee:", error);
      toast({
        title: "Failed to add employee",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsPasswordVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="employee@itmgmtsol.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isPasswordVisible && tempPassword && (
          <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">Temporary Password</h3>
            <div className="flex items-center">
              <code className="bg-white p-2 rounded border font-mono text-sm flex-1 overflow-x-auto">
                {tempPassword}
              </code>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                className="ml-2"
                onClick={copyPasswordToClipboard}
              >
                <ClipboardCopy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              Important: Note this password now. The employee will need it after clicking the verification link in their email.
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Employee"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EmployeeForm;
